import {
  AddressWithType,
  Good,
  Maybe,
} from '../freedomQl/__generated__/graphql';

import { OpenState } from '@/config/constants';

export type KeyValue = {
  [key: string]: any | undefined;
};

export type ErrorResponse = {
  error: {
    code: number;
    text: string;
  };
};

export type SuccessResponse = {
  success: boolean;
};

export const isErrorResponse = (model: unknown): model is ErrorResponse => {
  const possibleError = model as ErrorResponse;
  return (
    possibleError.error?.code !== undefined &&
    possibleError.error?.text !== undefined
  );
};

export type Image = {
  mimetype: string;
  image: string;
};

export type AdflexSessionRequest = {
  currency: string;
  value: string;
  email?: string;
  mobile?: string;
  contactRef?: number;
};

export type AdflexSessionResponse = {
  sessionId: string;
  transactionGuid: string;
  transactionReference: string;
};

export type IntegraContactInfo = {
  contactRef?: number;
  name: string;
  telephone?: string;
  mobile: string;
};

export type BookingFlow = {
  bookingTypes: BookingTemplate[];
};

export type BookingTemplate = {
  id: string;
  label: string;
  icon?: string;
  info?: string;
  imgUrl?: string;
  sections: BookingSection[];
  order: number;
};

export type BookingSection = {
  name: string;
  label: string;
  headerInputs?: string[];
  watcherInputs?: string[];
  icon?: string;
  inputs: BookingInput[] | undefined;
  order: number;
  openState?: OpenState;
  panel?: number;
};

export type OpenStates = {
  when: OpenState;
  jobFlags: OpenState;
  where: OpenState;
  goods: OpenState;
  payment: OpenState;
  tariffSlideshow: OpenState;
  references: OpenState;
  contact: OpenState;
  jobExtras: OpenState;
};

export type InputParams = {
  options?: BookingParamOption[];
  visible?: BookingParam[];
  compulsory?: BookingParam[];
  filters?: BookingParam[];
  addressTypes?: BookingParam[];
  tooltip?: BookingParam[];
  sort?: BookingParam[];
  validated?: boolean; // hydrated for refs
  format?: Maybe<string>; // hydrated for refs
  note?: Maybe<string>; // hydrated for job flags
  openState?: BookingParam[]; // for stop inputs
};

export type BookingInput = {
  id?: number; // hydrated for job flags + goods flags + refs
  dataName: string;
  label: string;
  type: string;
  subType?: any;
  compulsory?: number; // 0 | 1
  default?: any;
  min?: any;
  max?: any;
  params?: InputParams;
  order: number;
  visible?: number; // 0 | 1
};

export type BookingParam = {
  reference: string;
  code: string;
  label: string;
  icon?: string;
};

export type BookingParamOption = {
  reference: string;
  code: string | number;
  label: string;
  icon?: string;
};

export type IntegraJob = {
  dt: string;
  waitAndReturn?: boolean;
  stops: IntegraStop[];
  goods: Good[];
  refs: BookingRef[];
  flags: BookingFlag[];
  mop: string;
  contact?: BookingContact;
  card?: CardTokenInfo;
  cardReference?: number;
  despatchInstructions?: string;
  extras?: BookingExtra[];
  vouchers?: Voucher[];
  tc?: boolean;
  jobQuoteRef?: number;
};

export type CardTokenInfo = {
  transactionGuid: string;
  transactionReference: string;
  token: string;
};

export type Booking = {
  ref?: number;
  tariff: string;
} & IntegraJob;

export type IntegraAddress = {
  name?: string;
  add1?: string;
  add2?: string;
  add3?: string;
  add4?: string;
  postcode: string;
  lat: number;
  long: number;
  gridX?: number | null;
  gridY?: number | null;
  code?: string;
  country?: string;
  countryCode?: string;
  notes?: string;
};

// Stop addresses are sent in a different format to IntegraLink
export type IntegraStop = Omit<BookingStop, 'address'> & {
  addr?: IntegraAddress;
};

export type BookingRef = {
  id: number;
  valueId?: number;
  value?: string;
};

export type BookingFlag = {
  id: number;
  value: boolean;
};

export type BookingExtra = {
  code: string;
  value: any;
};

export type Voucher = {
  voucherRef: number;
  voucherCode: string;
};

export type BookingQuoteBody = {
  tariffs: string[];
  job: IntegraJob;
};

export type QuoteBeforeInfo = {
  net?: number;
  vat?: number;
  total?: number;
  discount?: number;
  currencyNet?: number;
  currencyVat?: number;
  currencyTotal?: number;
  currencyDiscount?: number;
};

export type BookingQuote = {
  section: string;
  showPrice: boolean;
  tariff: string;
  pickupTime?: string;
  delay?: number;
  delayMessage?: string;
  delayEarliest?: string;
  delayCurrentEarliest?: string; // Will always display the current earliest pickup time
  preventJob: boolean;
  tariffSort: number;
  mop: string;
  priceNet?: number;
  priceVat?: number;
  total?: number;
  description: string;
  currencyCode: string;
  warning?: string;
  note: string;
  quoteBefore?: QuoteBeforeInfo;
  obtainedDt: string;
};

export type BookingQuoteResponse = {
  journeyTime?: number;
  distance?: number;
  quotes?: BookingQuote[];
  vouchers?: { voucherRef: number }[];
};

export type IntegraAuthToken = string;

export type Response = {
  jwt?: IntegraAuthToken;
};

export type NewBookingResponse = {
  ref: number;
  link: string;
};

export type EditBookingResponse = SuccessResponse;

export type IntegraAuth = {
  sessID: number;
  sessCK: string;
};

export type IntegraCredentialSession = {
  reference: number;
  sessionRef: number;
  sessionKey: string;
  createdUTC: string;
  expiryUTC: string;
  closedUTC: number;
};

export type IntegraPayment = {
  defaultMOP?: string;
  allowedMOP?: AllowedMop[];
};

export type AllowedMop = {
  code?: string | null;
  description?: string | null;
};

export type IntegraAccount = {
  contactRef?: number | null;
  accountRef?: string | null;
  description?: string | null;
  email?: string | null;
  signUp?: {
    signUpByPin?: number | null;
    signUpByCode?: number | null;
    signUpByDomain?: number | null;
  } | null;
} & IntegraAccountSettings;

export type IntegraAccountSettings = {
  priceIncTax?: boolean | null;
  defaultContactIsPassenger?: boolean | null;
  signupDefaultGroupRef?: number | null;
};

export type IntegraHost = {
  coreLib?: string;
  integraLib?: string;
};

export type Ref = {
  id?: number | null;
  description?: string | null;
  prompt?: string | null;
  disabled: boolean;
  min?: number | null;
  max?: number | null;
  check: boolean;
  compulsory: boolean;
  allowDef: boolean;
  def?: string | null;
  defId?: number | null;
  search: boolean;
  ddl?: string | null;
  masks?: {
    from?: string | null;
    to?: string | null;
    message?: string | null;
  }[];
};

export type Tariff = {
  reference: number | null;
  tariff?: string | null;
  label?: string | null;
  description?: string | null;
  sortOrder?: number | null;
  viewOnly: boolean;
  warningMsg?: string | null;
  defaultGoodsType?: number | null;
  maxPassenger?: number | null;
  maxGoodsQty?: number | null;
  maxGoodsDepth?: number | null;
  maxGoodsWidth?: number | null;
  maxGoodsHeight?: number | null;
  maxGoodsWeight?: number | null;
  maxGoodsCartons?: number | null;
  cutoffTime?: string | null;
  url?: string | null;
  imgUrl?: string | null;
  appImgUrl?: string | null;
  appImgUrlUpdatedDt?: string | null;
};

export type IntegraGoodsType = {
  id: number;
  code?: string | null;
  description?: string | null;
};

export type IntegraUser = {
  contactRef?: number | null;
  accountRef?: string | null;
  name?: string | null;
  telephone?: string | null;
  mobile?: string | null;
  fax?: string | null;
  title?: string | null;
  email?: string | null;
  payment?: IntegraPayment;
  callCentre?: {
    number?: string | null;
    email?: string | null;
  };
  operatingArea?: {
    opArea?: number | null;
    lat?: number | null;
    long?: number | null;
  };
  tracking?: {
    jobRefresh?: number | null;
    trackRefresh?: number | null;
  };
  host?: IntegraHost;
  refs: Ref[];
  tariffs: Tariff[];
  goodsTypes: IntegraGoodsType[];
};

export type DistDesc = 'Miles' | 'Kilometres';

export type TimeDesc = 'Minutes' | 'Hours';

export type Settings = {
  systemName?: string | null;
  distDesc: DistDesc;
  timeDesc: TimeDesc;
  otherRefDesc?: string | null;
  accountDeptDesc?: string | null;
  version?: string | null;
  goodsDesc?: string | null;
  goodsInfo1Desc?: string | null;
  goodsInfo2Desc?: string | null;
  goodsInfo3Desc?: string | null;
  defaultGoodsType?: number | null;
  defaultCountryCode?: string | null;
  minimumJobStops?: number | null;
  currency?: string | null;
  currencyCode?: string | null;
  allowedGoodsTypes?: string | null;
  currentUTC?: string | null;
};

export type IntegraProfile = {
  reference: number;
  loggedIn: boolean;
  accounts: IntegraAccount[];
  credentialSession: IntegraCredentialSession;
  userSession?: IntegraAuth;
  user: IntegraUser;
  settings?: Settings;
  loqate?: ApiDetails;
  what3words?: ApiDetails;
};

export type SessionLogonRequest = {
  email?: string;
  guest?: boolean;
  name?: string;
  telephone?: string;
  mobile?: string;
  contactRef?: number;
  accountRef?: string;
  accountSignUpPIN?: number;
  accountSignUpCode?: string;
};

export type SessionLogonResponse = Response & {
  profile: IntegraProfile;
};

export type ApiDetails = {
  domain?: string | null;
  account?: string | null;
  key?: string | null;
  countries?: string | null;
  countriesDescription?: string | null;
};

export type ReportRunRequest = {
  reportRef: number;
  filters: ReportFilterValue[];
};

export type ReportFilterValue = {
  ident: string;
  value: string;
};

export type ReportHeader = {
  reference: number;
  account_ref: string;
  contact_ref: number;
  description: string;
  noupdate: string;
  report_type: number;
  html_path: string;
};

export type ReportColumn = {
  reference: number;
  report_ref: number;
  fieldposn: number;
  field: string;
  formatting: string;
  heading: string;
  compValueDataType?: string | null;
};

export type ReportFilter = {
  reference: number;
  report_ref: number;
  field: string;
  criteria: string;
  compvalue: string;
  compValueDataType?: string;
  fieldposn: number;
  prompt: string;
  compulsory: number;
  valueIdent: string;
  heading: string;
};

export type GenericObject = { [key: string]: any };

export type ReportListResponse = ReportHeader[];

export type ReportRunResponse = {
  reportHeader: ReportHeader;
  reportColumns: ReportColumn[];
  reportFilters: ReportFilter[];
  reportData: GenericObject[];
};

export type ContactCardAddRequest = {
  contactRef: number;
  default?: boolean;
  nameOnCard?: string;
  card: CardTokenInfo;
};

export type ContactCardAddResponse = Response & {
  reference: number;
};

export type ContactCardDeleteRequest = {
  contactRef?: number;
  reference: number;
};

export type ContactCardMakeDefaultRequest = {
  contactRef?: number;
  reference: number;
};

export type CancelBookingRequest = {
  ref: number;
  reason?: string;
};

export type CancelBookingResponse = {
  ref: number;
  result: string;
};

export type ContactAddressAddRequest = {
  contactRef: number;
  default?: boolean;
  addr: IntegraAddress;
};

export type ContactAddressAddResponse = {
  reference: number;
};

export type ContactAddressDeleteRequest = {
  contactRef?: number;
  reference: number;
};

export type JourneyMakeFavouriteRequest = {
  reference: number;
  description?: string;
};

export type JourneyClearFavouriteRequest = {
  reference: number;
};

export type ContactAddressMakeDefaultRequest = {
  contactRef?: number;
  reference: number;
};

export type ContactAddressClearDefaultRequest = {
  contactRef?: number;
  reference: number;
};

export type ContactAddressDeleteResponse = SuccessResponse;

export type JourneyMakeFavouriteResponse = SuccessResponse;

export type JourneyClearFavouriteResponse = SuccessResponse;

export type ContactAddressMakeDefaultResponse = SuccessResponse;

export type ContactAddressClearDefaultResponse = SuccessResponse;

export type ContactAddressEditRequest = {
  contactRef: number;
  default?: boolean;
  addr: Partial<IntegraAddress> & { ref: number };
};

export type ContactAddressEditResponse = SuccessResponse;

export type FlightLookupRequest = {
  flightNumber: string;
  flightDt?: string;
  departure?: boolean;
};

export type FlightResponse = {
  flightNumber: number;
  carrierCode: string;
  departTerminal: string;
  arriveTerminal: string;
  localDepartTime: string;
  localArriveTime: string;
  localDepartDate: string;
  localArriveDate: string;
  localDepartDateDesc: string;
  localArriveDateDesc: string;
  departAirportDesc: string;
  arriveAirportDesc: string;
  departAirportCode: string;
  arriveAirportCode: string;
  departAddressRef: number;
  arriveAddressRef: number;
};

export type FlightLookupResponse = {
  flightSummary: string;
  flights: FlightResponse[];
};

export type BookingStop = {
  address?: AddressWithType;
  flight?: FlightDetail;
  notes?: string;
  requiredFrom?: string;
  requiredTo?: string;
  contactName?: string;
  contactEmail?: string;
  contactMobile?: string;
  contactTelephone?: string;
  asDirected?: boolean;
  asDirectedTime?: number;
};

export type FlightDetail = {
  code: string;
  fromToCity: string;
  dateTime: string;
  terminal: string;
  departure: boolean;
  number: number;
  carrierCode: string;
  scheduledDepartureDt: string;
  scheduledArrivalDt: string;
  departAirportCode: string;
  arriveAirportCode: string;
  holdOff: number;
};

export type BookingContact = {
  ref: number;
  name?: string;
  phone?: string;
  mobile?: string;
  email?: string;
};

export type TableCol = {
  field: string;
  header: string;
  type?: string | null;
  format?: string | null;
};

export type TableData = {
  reference: number;
  contactRef?: number | null;
} & GenericObject;

export type ContactLinkReqRequest = {
  contactRef?: number;
  contactEmail: string;
};

export type ContactLinkReqResponse = SuccessResponse;

export type ContactLinkListRequest = {
  contactRef?: number;
};

export type ContactLink = {
  contactRef: number;
  code?: string;
  name?: string;
  tel?: string;
  mob?: string;
  fax?: string;
  email?: string;
};

export type PendingContactLink = ContactLink & {
  accountRef: string;
  authGuid: string;
};

export type ContactLinkPendingListResponse = Response & {
  contacts: PendingContactLink[];
};

export type ContactLinkListResponse = Response & {
  contacts: ContactLink[];
};

export type ContactLinkAuthoriseRequest = {
  contactRef?: number;
  contactEmail?: string;
  authGuid: string;
  authorise: boolean;
};

export type ContactLinkAuthoriseResponse = SuccessResponse;

export type SendConfirmationRequest = {
  jobRef: number;
  email: string;
};

export type SendConfirmationResponse = Response & {
  code: number;
  description: string;
};

export type Populate3waDetailsRequest = {
  words: string;
};

export type Populate3waDetailsResponse = Response & {
  addressName: string;
  address1?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  postcode: string;
  country?: string;
  countryCode?: string;
  lat: number;
  long: number;
  typeCode: string;
};

export type SwitchToBrowserResponse = Response & {
  result: string;
  err?: string;
  param1?: string;
  param2?: string;
};
